import * as graphql from 'graphql.js';
import * as waitUntil from 'async-wait-until';
import { BrowserStorage } from 'elc-universal-utils';
import { IFilterCheckbox } from '../internal/views/product-filters/IProductFilters';
import { ProductQueryStashingService } from './ProductQueryStashingService';
import { IShadeData } from '../internal/domain/entities/Shade';
import { ISkuData, ISku } from '../internal/domain/entities/Sku';
import { IBenefit } from '../internal/domain/entities/Benefit';
import { IProductReview } from '../internal/domain/entities/ProductReview';
import { emit, events } from 'elc-service-bus';
import { ICategory } from '../internal/domain/entities/Category';
import { IKeyValue, IKeyName } from '../internal/domain/entities/BasicEntities';
import { IMedia } from '../internal/domain/entities/Media';
import { IAsset } from '../internal/domain/entities/Asset';
import { IProductUsage } from '../internal/domain/entities/ProductUsage';
import { ITag } from '../internal/domain/entities/Tag';
import { ICrossSell, ICrossSellData } from '../internal/domain/entities/CrossSell';
import { IProductFinish } from '../internal/domain/entities/ProductFinish';
import { sanitizeJsonForQuery } from '../internal/utils/ContentFormatter';
import { IFeatures } from '../internal/constants/Features';
import { IQueryTypeFragments, QueryFragments } from './QueryFragments';
import { QueryType } from './features/Default';
import { IProductSkin } from '../internal/domain/entities/ProductSkin';
import { IProductShadeGroup } from '../internal/domain/entities/ProductShadeGroup';
import { IDescriptionStatement } from '../internal/domain/entities/DescriptionStatement';

export type QL = string;

export interface IVars {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [index: string]: any;
}

export type IFragment = string;

export interface ICoreProduct {
    benefit: IBenefit;
    brand: string;
    collection?: IKeyValue[];
    coverage?: IKeyValue[];
    cross_sell: ICrossSell[];
    default_category: ICategory;
    description: string;
    display_name: string;
    finish?: IProductFinish[];
    form?: IKeyValue[];
    fragrance_family?: IKeyValue[];
    fragrance_notes: IKeyValue[];
    free_from?: IKeyValue[];
    description_statements?: IDescriptionStatement[];
    is_hazmat: boolean;
    key_ingredient: string;
    key_ingredients?: IKeyValue[];
    media: IMedia;
    product_badge: string;
    product_badge_image: string;
    product_id: string;
    product_url: string;
    reviews: IProductReview;
    seo_meta_description?: string;
    seo_meta_keywords: string;
    shade_groups: IProductShadeGroup[];
    short_description: string;
    skin?: IProductSkin;
    skin_goal?: IKeyValue[];
    skus: ISkuCollection;
    sub_display_name: string;
    sub_header: string;
    tags: ITagCollection;
    usage: IProductUsage[];
    usage_options: IKeyValue[];
}

export interface IProductData {
    brand: string;
    crossSell: ICrossSellData[];
    defaultMediumImage: IAsset;
    defaultLargeImage: IAsset;
    defaultCategory: ICategory;
    defaultSku: ISkuData;
    defaultOriginalPrice: number;
    defaultPrice: number;
    defaultSize: string;
    defaultImage: IAsset;
    description: string;
    displayName: string;
    isHazmat: boolean;
    media: IMedia;
    productBadge: string;
    productId: string;
    productUrl: string;
    shades: IShadeData[];
    sizes: IKeyValue[];
    skus: ISkuData[];
    skuPromoMessage: string;
    subDisplayName: string;
    seoMetaDescription: string;
    seoMetaKeywords: string;
    shortDescription: string;
    subHeader: string;
    tags: ITag[];
    usage: IProductUsage[];
    benefit: IBenefit;
    keyIngredient: string;
    keyIngredients: IKeyValue[];
    reviews: IProductReview;
    finish?: IProductFinish[];
    coverage?: IKeyValue[];
    form: IKeyValue[];
    skinConcern: IKeyValue[];
    fragranceFamily: IKeyValue[];
    skinGoal: IKeyValue[];
    collections: IKeyValue[];
    freeFrom: IKeyValue[];
    descriptionStatements?: IDescriptionStatement[];
    skin: IProductSkin;
    fragranceNotes: IKeyValue[];
    occasion: IKeyValue[];
    usageOptions: IKeyValue[];
}

export interface ISkuCollection {
    total: number;
    first: ISku;
    items: ISku[];
}

export interface ITagCollection {
    total: number;
    first: ITag;
    items: ITag[];
}

export interface IPerlgem {
    SKU_BASE_ID: number;
}

export type IColorFamily = IKeyName;
export type IFinishOption = IKeyValue;
export type IUndertone = IKeyValue;
export type IFinish = IKeyValue;

export interface ISize {
    label: string;
}

export interface IResultProducts {
    products: {
        items: ICoreProduct[];
    };
}

export interface IResultProductById {
    product: ICoreProduct;
}

export interface IProdcatApiSdk {
    baseUrl: string;
    businessUnit: string;
    clientId: string;
}

export interface IClient {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    query<T>(query: QL, vars?: IVars): Promise<any>;
    createFragment(fragment: QL): IFragment;
}

export interface IPromise {
    product: ICoreProduct;
}

export interface IParamsById {
    productIds?: string[];
    skuIds?: string[];
    categoryIds?: string[];
}

export enum ItemTypes {
    sku = 'sku',
    product = 'product'
}
interface IItemParams {
    id: string;
}
export interface IProductParams extends IItemParams {
    skusToDisplay?: string[];
    defaultSkuId?: string;
    featured?: boolean;
}
export type ISkuParams = IItemParams;

type ItemByType = { [K in ItemTypes]: ISkuParams[] | IProductParams[] };
export interface IItemByType extends ItemByType {
    [K: string]: ISkuParams[] | IProductParams[];
}

interface IIncludesQuery {
    in: string[];
}
interface ISkuIdFilterQuery {
    sku_id?: IIncludesQuery;
    inventory_status?: IIncludesQuery;
}
interface ISkuFilterQuery {
    filter: ISkuIdFilterQuery;
    includeInnerHits: boolean;
}

export enum TagFilterType {
    id = 'id',
    key = 'key'
}

interface ITagFilterQuery {
    id?: IIncludesQuery;
    key?: IIncludesQuery;
}

interface ITagsFilterQuery {
    filter: ITagFilterQuery;
    includeInnerHits: boolean;
}

interface ITagsFilter {
    tags: ITagsFilterQuery;
    skus?: ISkuFilterQuery;
}

interface IProductsFilter {
    product_id: IIncludesQuery;
    skus?: ISkuFilterQuery;
}

interface ISkuFilter {
    skus: ISkuFilterQuery;
}

export interface IItem {
    type: ItemTypes;
    params: ISkuParams | IProductParams;
}

export interface IItemsParams {
    items: IItem[];
    inventoryStatuses?: string[];
}

export interface IAppliedFilters {
    appliedOptions: IFilterCheckbox[];
}

export interface IApiConfig {
    graphqlExtensionVersion?: string;
    clientId?: string;
    baseUrl?: string;
    businessUnitId?: string;
    features?: IFeatures;
}

export interface IConfigFeature {
    getFragment(): string;
}

export const AUTH_TOKEN_COOKIE_NAME = 'elc-auth-token';
export const UNAUTHORIZED_MESSAGE_401 = 'Unauthorized';
export const UNAUTHORIZED_MESSAGE_403 =
    'User is not authorized to access this resource with an explicit deny';

const PRODUCT_QUERY_DEBOUNCE_VALUE = 500;

export class ProdcatApiSdk {
    // todo: dynamically derive the appropriate versions from the config service
    private configs: IApiConfig;
    private productQueryStashingService: ProductQueryStashingService;
    // we should be able to remove this as it's gonna be per controller instance
    private queryFragments: QueryFragments;
    public coreVersion: string = 'v1';
    private apiVersion: string = 'v2';
    private skuStatusFilter?: string[];

    static splitItemByType(items: IItem[]) {
        const itemsByType = Object.keys(ItemTypes).reduce((keys, key) => {
            keys[key] = [];

            return keys;
        }, {} as IItemByType);

        return items.reduce((separatedItems, item: IItem) => {
            separatedItems[item.type].push(item.params);

            return separatedItems;
        }, itemsByType);
    }

    static createItemParamsFromIds = (ids: string[], type: ItemTypes) => <IItem[]>ids.map((id) => ({
            type,
            params: { id }
        }));

    // Remove this method after full support of items key instead of paramsById
    static getItemsFromParamsById(paramsById: IParamsById, featuredProductId?: string) {
        const { productIds = [], skuIds = [] } = paramsById;
        const items: IItem[] = featuredProductId
            ? [{ type: ItemTypes.product, params: { id: featuredProductId, featured: true } }]
            : [];

        productIds.forEach((id) => {
            if (id !== featuredProductId) {
                items.push({ type: ItemTypes.product, params: { id } });
            }
        });
        skuIds.forEach((id) => {
            items.push({ type: ItemTypes.sku, params: { id } });
        });

        return items;
    }

    constructor(config: IProdcatApiSdk) {
        const { baseUrl, businessUnit, clientId } = config;
        this.configs = {
            baseUrl,
            businessUnitId: businessUnit,
            clientId
        };
        this.productQueryStashingService = new ProductQueryStashingService();
    }

    public setQueryFragments(queryFragments: QueryFragments) {
        this.queryFragments = queryFragments;
    }

    public setConfigs(config: Partial<IApiConfig>) {
        this.configs = { ...this.configs, ...config };
    }

    private get prodcatUrl() {
        return `stardust-prodcat-product-${this.apiVersion}/graphql/core/${this.coreVersion}/extension/${this.configs.graphqlExtensionVersion}`;
    }

    private get composedUrl() {
        return `${this.configs.baseUrl}/${this.prodcatUrl}`;
    }

    private get headers() {
        return {
            'business-unit': this.configs.businessUnitId,
            'Cache-Control': 'no-cache',
            ClientId: this.configs.clientId,
            authorizationtoken: BrowserStorage.getCookie(AUTH_TOKEN_COOKIE_NAME)
        };
    }

    private get client() {
        return graphql(this.composedUrl, {
            headers: this.headers,
            fragments: {
                ...this.queryFragments.enabledFragments
            },
            asJSON: true
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public onRequestError = async (err: { message: string }, callback: () => Promise<any>) => {
        if (err.message === UNAUTHORIZED_MESSAGE_401 || err.message === UNAUTHORIZED_MESSAGE_403) {
            emit(events.UNAUTHORIZED_REQUEST);
            await waitUntil(() => BrowserStorage.getCookie(AUTH_TOKEN_COOKIE_NAME));

            return callback();
        } else {
            return Promise.reject(err);
        }
    };

    private getDebouncedProducts = async () => {
        const { items, queue } = this.productQueryStashingService.prepareProductsQuery();
        const products = await this.getProductsById(items, true, this.skuStatusFilter);

        this.productQueryStashingService.resolveQueue(products, queue);
    };

    private getProductsWithSkus(products: IProductParams[]) {
        return products.reduce(
            (skusArray: string[][], product: IProductParams) => {
                const { skusToDisplay = [], id } = product;
                if (skusToDisplay.length > 0) {
                    skusArray[0].push(id);
                    skusArray[1] = skusArray[1].concat(skusToDisplay);
                }

                return skusArray;
            },
            [[], []]
        );
    }

    private buildProductQueryFilter = (
        items: IItem[],
        includeInnerHits: boolean = true,
        statusFilter: string[] = []
    ): string => {
        const {
            sku: skus,
            product: products
        }: { sku: ISkuParams[]; product: IProductParams[] } = ProdcatApiSdk.splitItemByType(items);

        const [productIdsWithSku, productsWithSkus] = this.getProductsWithSkus(products);
        const filteredProducts = products.filter(
            (product) => !productIdsWithSku.includes(product.id)
        );
        const hasProductIdParam = filteredProducts.length > 0;
        const hasSkusIdParam = skus.length > 0;
        const hasInventoryStatParam = statusFilter.length > 0;
        const skusIds = hasSkusIdParam ? skus.map(({ id }) => id) : [];

        const filters: (IProductsFilter | ISkuFilter)[] = [];
        if (hasSkusIdParam || productsWithSkus.length > 0) {
            const skusFilter: ISkuFilter = {
                skus: {
                    filter: {
                        sku_id: {
                            in: [...skusIds, ...productsWithSkus]
                        }
                    },
                    includeInnerHits
                }
            };

            if (hasInventoryStatParam) {
                skusFilter.skus.filter.inventory_status = {
                    in: statusFilter
                };
            }

            filters.push(skusFilter);
        }
        if (hasProductIdParam) {
            const productsFilter: IProductsFilter = {
                product_id: {
                    in: filteredProducts.map(({ id }) => id)
                }
            };
            if (hasInventoryStatParam) {
                productsFilter.skus = {
                    filter: {
                        inventory_status: {
                            in: statusFilter
                        }
                    },
                    includeInnerHits
                };
            }
            filters.push(productsFilter);
        }

        return sanitizeJsonForQuery(JSON.stringify(filters));
    };

    private buildProductQueryFilterByTags = (
        tags: string[],
        filterById: boolean = false,
        statusFilter: string[] = []
    ): string => {
        const hasInventoryStatParam = statusFilter.length > 0;

        const productsFilter: ITagsFilter = {
            tags: {
                filter: {
                    [filterById ? TagFilterType.id : TagFilterType.key]: {
                        in: tags
                    }
                },
                includeInnerHits: true
            }
        };

        if (hasInventoryStatParam) {
            productsFilter.skus = {
                filter: {
                    inventory_status: {
                        in: statusFilter
                    }
                },
                includeInnerHits: true
            };
        }

        return sanitizeJsonForQuery(JSON.stringify(productsFilter));
    };

    public getProductsById = async (
        items: IItem[],
        includeInnerHits: boolean,
        statusFilter?: string[],
        queryFragments?: IQueryTypeFragments
    ): Promise<ICoreProduct[]> => {
        try {
            // skus total
            const response = await this.client(`
                {
                    products(filter: ${this.buildProductQueryFilter(
                        items,
                        includeInnerHits,
                        statusFilter
                    )}) {
                        items {
                            ${
                                queryFragments
                                    ? queryFragments[QueryType.product]
                                    : this.queryFragments.queryFragments[QueryType.product]
                            }
                            skus {
                                total
                                items {
                                    ${
                                        queryFragments
                                            ? queryFragments[QueryType.skus]
                                            : this.queryFragments.queryFragments[QueryType.skus]
                                    }
                                }
                            }
                        }
                    }
                }
            `)();

            return Promise.resolve(response.products.items);
        } catch (e) {
            return this.onRequestError(e, this.getProductsById.bind(null, items));
        }
    };

    public getProductById = async (
        productId: string,
        useDebounce?: boolean,
        inventoryStatuses?: string[],
        queryFragments?: IQueryTypeFragments
    ): Promise<ICoreProduct> => {
        if (useDebounce) {
            this.skuStatusFilter = inventoryStatuses;
            this.productQueryStashingService.debounce(
                this.getDebouncedProducts,
                PRODUCT_QUERY_DEBOUNCE_VALUE
            );

            return this.productQueryStashingService.createQueryPromise(productId);
        }

        const productItem = ProdcatApiSdk.createItemParamsFromIds([productId], ItemTypes.product);
        const products = await this.getProductsById(
            productItem,
            true,
            inventoryStatuses,
            queryFragments
        );
        const [product] = products || [];

        return product;
    };

    public getProductsByTag = async (
        productTags: string[],
        filterById?: boolean,
        inventoryStatuses?: string[],
        queryFragments?: IQueryTypeFragments
    ): Promise<ICoreProduct[]> => {
        try {
            const response = await this.client(`
            {
                products(filter: ${this.buildProductQueryFilterByTags(
                    productTags,
                    filterById,
                    inventoryStatuses
                )}, sort: [
                    { tags: { product_display_order: ASCENDING } },
                ]) {
                    items {
                        ${
                            queryFragments
                                ? queryFragments[QueryType.product]
                                : this.queryFragments.queryFragments[QueryType.product]
                        }
                        skus {
                            total
                            items {
                                ${
                                    queryFragments
                                        ? queryFragments[QueryType.skus]
                                        : this.queryFragments.queryFragments[QueryType.skus]
                                }
                            }
                        }
                    }
                }
            }
        `)();

            return Promise.resolve(response.products.items);
        } catch (e) {
            return this.onRequestError(e, this.getProductsByTag.bind(null, productTags));
        }
    };

    /* only used by ProductSkuRepository */
    public getProducts = async (
        paramsById: IParamsById,
        queryFragment: string = ''
    ): Promise<ICoreProduct[]> => {
        const productIds = paramsById.productIds || [];
        const skuIds = paramsById.skuIds || [];
        const categoryIds = paramsById.categoryIds || [];

        const finalProductIds = JSON.stringify(productIds);
        const finalSkuIds = JSON.stringify(skuIds);
        const finalCategoryIds = JSON.stringify(categoryIds);
        try {
            const response = await this.client(`
            {
                products(filter: [
                        { product_id: { in: ${finalProductIds} } },
                        { tags: { filter: { id: { in: ${finalCategoryIds} } } } },
                        { skus: { filter: { sku_id: { in: ${finalSkuIds} } } } }
                    ]) {
                        items {
                            ${queryFragment}
                            skus {
                                items {
                                    sku_id
                                    sku_badge
                                }
                            }
                        }
                    }
            }`)();

            return Promise.resolve(response.products.items);
        } catch (e) {
            return this.onRequestError(e, this.getProducts.bind(null, paramsById, queryFragment));
        }
    };
}
